import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from "@angular/core";
import { StrategyDspFunnel } from "@front/m19-api-client";
import { DspStrategyEx } from "@front/m19-models/strategy/DspStrategyEx";
import { DspStrategyService } from "@front/m19-services";
import { IButtonDirective, MODAL_DATA, ModalOptions, ModalRef, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DspFunnelSelectorComponent } from "../dsp-funnel/dsp-funnel-selector.component";

export interface DspFunnelSelectorModalData {
  strategy: DspStrategyEx;
  organizationId: number;
}

@Component({
  selector: "app-dsp-funnel-selector-modal",
  template: `
    <div class="flex flex-col gap-4 pb-4" *transloco="let t">
      @if (isAwareness()) {
        <div class="text-sm font-medium text-gray-800">
          {{ t("dsp-funnel-selector-modal.you_cannot_change_the_funnel_for_an_awareness_strategy") }}
        </div>
      }
      <app-dsp-funnel-selector
        [(selectedFunnel)]="selectedFunnel"
        [disableAwareness]="!isAwareness()"
        [class.disabled]="isAwareness()"
      />
      <div class="mt-2 flex justify-end gap-2">
        <button iButton variant="secondary" size="md" (click)="modalRef.close()">{{ t("common.close") }}</button>

        <button
          iButton
          type="submit"
          size="md"
          [disabled]="!selectedFunnel() || isUpdatingFunnel()"
          [loading]="isUpdatingFunnel()"
          (click)="save()"
        >
          {{ t("common.save") }}
        </button>
      </div>
    </div>
  `,
  imports: [IButtonDirective, TranslocoDirective, DspFunnelSelectorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspFunnelSelectorModalComponent {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "sm" };

  private readonly modalData = inject<DspFunnelSelectorModalData>(MODAL_DATA);
  private readonly strategyService = inject(DspStrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  readonly modalRef = inject(ModalRef);

  private readonly strategy = this.modalData.strategy;
  readonly selectedFunnel = model<StrategyDspFunnel | undefined>(this.strategy.dspFunnel);
  readonly isUpdatingFunnel = signal(false);

  readonly isAwareness = computed(() => this.strategy.dspFunnel === StrategyDspFunnel.AWARENESS);

  save() {
    const funnel = this.selectedFunnel();
    if (!funnel) {
      return;
    }
    this.isUpdatingFunnel.set(true);

    this.strategyService
      .updateStrategy({
        accountId: this.strategy.accountId,
        marketplace: this.strategy.marketplace,
        organizationId: this.modalData.organizationId,
        strategyId: this.strategy.strategyId,
        dspFunnel: funnel,
        asinsToAdd: [],
        asinsToDelete: [],
      })
      .subscribe({
        next: () => {
          this.toasterService.success(this.translocoService.translate("strategy-page.funnel_updated"));
          this.modalRef.close(funnel);
        },
        error: (error) => {
          this.toasterService.error(error);
          this.isUpdatingFunnel.set(false);
        },
      });
  }
}
